import { flatten } from 'lodash';
import React from 'react';

import { useAsyncEffect } from '@/hooks/useAsyncEffect';
import { isCountry } from '@/types/country';
import { isNamedIdentified, NamedIdentified } from '@/types/namedIdentified';

export interface CountryCity {
  readonly name: string;
  readonly state: string;
}

export const useCountryCities = (country: string | undefined): readonly CountryCity[] => {
  const [cities, setCities] = React.useState<readonly CountryCity[]>([]);

  const countryKey = React.useMemo((): string => country?.toLowerCase() ?? '', [country]);

  const fetchCountryStates = React.useCallback(async (): Promise<readonly NamedIdentified[]> => {
    const response = await fetch(`/countries/${countryKey}/index.json`);
    const resolved = await response.json();
    if (!resolved) {
      return [];
    }

    if (isCountry(resolved)) {
      return resolved.states;
    }

    return [];
  }, [countryKey]);

  const fetchCountryCities = React.useCallback(async (): Promise<readonly CountryCity[]> => {
    const states = await fetchCountryStates();

    const fetchStateCities = async (state: string): Promise<readonly CountryCity[]> => {
      const stateKey = state.toLowerCase();
      const response = await fetch(`/countries/${countryKey}/${stateKey}/index.json`);
      const resolved = await response.json();
      if (!resolved) {
        return [];
      }

      if (Array.isArray(resolved) && resolved.every(isNamedIdentified)) {
        return resolved.map(
          (city: NamedIdentified): CountryCity => ({ name: city.name, state: state }),
        );
      }

      return [];
    };

    const groups = await Promise.all(
      states.map(
        (state: NamedIdentified): Promise<readonly CountryCity[]> => fetchStateCities(state.id),
      ),
    );

    return flatten(groups);
  }, [countryKey, fetchCountryStates]);

  useAsyncEffect(async (): Promise<void> => {
    const countryCities = await fetchCountryCities();
    setCities(countryCities);
  }, [fetchCountryStates]);

  return cities;
};
